import { MenuOutlined } from '@ant-design/icons';
import { Button, Dropdown, Spin } from 'antd';
import html2canvas from 'html2canvas';
import { jsPDF as PDF } from 'jspdf';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { ExportButtonBorder, ExportButtonIcon, ExportButtonShadow } from '../../styles';

const StyledButton = styled(Button)`
  color: ${ExportButtonIcon};
  border-color: ${ExportButtonBorder};
  border-radius: 3px;
  width: 18px !important;
  height: 18px;
  margin: 1px 12px;
  padding: 1px;

  .anticon {
    font-size: 10px !important;
  }
`;

const PDF_HEIGHT = 792;
const PDF_WIDTH = 612;
const PDF_MARGIN = 30;

const menuItemStyle = {
  fontSize: '12px',
  padding: '0 8px',
  fontFamily: 'Roboto',
};

/**
 * Converts an array of objects to a CSV string and triggers a download.
 * @param {Array<Object>} data - Array of row objects with consistent keys.
 * @param {string} filename - Filename without extension.
 */
const downloadCsv = (data, filename) => {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvRows = [
    headers.join(','),
    ...data.map((row) =>
      headers
        .map((h) => {
          const val = row[h] ?? '';
          // Wrap in quotes if the value contains commas, quotes, or newlines
          const str = String(val);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        })
        .join(','),
    ),
  ];

  const csvString = csvRows.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Reusable chart export menu component.
 *
 * Provides a dropdown button with options to export a chart as PNG, PDF, and optionally CSV.
 *
 * @param {Object} props
 * @param {React.RefObject} props.chartRef - Ref to the DOM element to capture for PNG/PDF export.
 * @param {string} props.filename - Base filename for exports (special chars will be sanitized).
 * @param {Array<Object>|Function} [props.csvData] - Data for CSV export. Can be an array of objects
 *   or a function that returns an array of objects. If not provided, CSV option is hidden.
 * @param {boolean} [props.enablePng=true] - Whether to show the PNG export option.
 * @param {boolean} [props.enablePdf=true] - Whether to show the PDF export option.
 * @param {boolean} [props.enableCsv=true] - Whether to show the CSV export option (requires csvData).
 */
function ChartExportMenu({
  chartRef,
  filename,
  csvData = null,
  enablePng = true,
  enablePdf = true,
  enableCsv = true,
}) {
  const [isDownloading, setDownloading] = useState(false);

  const sanitizedFilename = filename.replace(/[^A-Z0-9]+/gi, '_');

  const handleDownloadImage = async (extension) => {
    setDownloading(true);
    try {
      const element = chartRef.current;
      const canvas = await html2canvas(element);
      const chartImg = canvas.toDataURL('image/png', 1.0);

      if (extension === 'png') {
        const link = document.createElement('a');
        link.href = chartImg;
        link.download = `${sanitizedFilename}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (extension === 'pdf') {
        const pdf = new PDF('p', 'pt', 'letter');
        const innerHeight = PDF_HEIGHT - 2 * PDF_MARGIN;
        const innerWidth = PDF_WIDTH - 2 * PDF_MARGIN;
        const imgHeight = (innerWidth / canvas.width) * canvas.height;

        if (imgHeight > innerHeight) {
          let heightUnprinted = imgHeight;
          let position = PDF_MARGIN;
          while (heightUnprinted > 0) {
            pdf.addImage(chartImg, 'PNG', PDF_MARGIN, position, innerWidth, imgHeight);

            pdf.setFillColor(255, 255, 255);
            pdf.rect(0, 0, PDF_WIDTH, PDF_MARGIN, 'F');
            pdf.rect(0, PDF_HEIGHT - PDF_MARGIN, PDF_WIDTH, PDF_MARGIN, 'F');

            heightUnprinted -= innerHeight;
            position -= innerHeight;

            if (heightUnprinted > 0) {
              pdf.addPage();
            }
          }
        } else {
          pdf.addImage(chartImg, 'PNG', PDF_MARGIN, PDF_MARGIN, innerWidth, imgHeight);
        }

        pdf.save(`${sanitizedFilename}.pdf`);
      }
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadCsv = () => {
    const data = typeof csvData === 'function' ? csvData() : csvData;
    downloadCsv(data, sanitizedFilename);
  };

  const menuItems = [];

  if (enablePng) {
    menuItems.push({
      label: 'Export As PNG',
      key: 'png',
      onClick: () => handleDownloadImage('png'),
      style: menuItemStyle,
    });
  }

  if (enablePdf) {
    menuItems.push({
      label: 'Export As PDF',
      key: 'pdf',
      onClick: () => handleDownloadImage('pdf'),
      style: menuItemStyle,
    });
  }

  if (enableCsv && csvData) {
    menuItems.push({
      label: 'Export As CSV',
      key: 'csv',
      onClick: handleDownloadCsv,
      style: menuItemStyle,
    });
  }

  if (menuItems.length === 0) return null;

  return (
    <Spin size="small" spinning={isDownloading}>
      <Dropdown
        menu={{
          items: menuItems,
          selectable: false,
          style: {
            border: '1px solid',
            borderRadius: '0',
            boxShadow: `${ExportButtonShadow} 2px 2px 5px`,
            padding: '3px',
          },
        }}
        placement="bottomRight"
      >
        <StyledButton icon={<MenuOutlined />} data-html2canvas-ignore />
      </Dropdown>
    </Spin>
  );
}

ChartExportMenu.propTypes = {
  chartRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  filename: PropTypes.string.isRequired,
  csvData: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object), PropTypes.func]),
  enablePng: PropTypes.bool,
  enablePdf: PropTypes.bool,
  enableCsv: PropTypes.bool,
};

export default ChartExportMenu;
